import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { restAuthRefTypes } from 'app/model/linkedservice/rest-service.linkedservice';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { authenticationReferenceMethodOptions, getAuthHeader, parameterizableAzureCloudTypeMetadata } from '../consts/metadata-consts';
import { GlobalVariables } from 'app/services/globals';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { ConnectionAuthKind, CredentialTypeOptions, compressionCodecType, Owned, restAuthTypes, servicePrincipalCredentialTypes, Type } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { RestCallSettings } from 'app/model/dataflow/dataset-settings/restCall-settings';
import { documentFormOptions } from 'app/authoring/component/transformations/complex-type-utils';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { RestSettings } from 'app/model/dataflow/dataset-settings/rest-settings';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { IDataflowTypeMetadataSink, IDataflowTypeMetadataSource } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { CommonResourceKind as ResourceKind, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { sharePointSpnRetirementDocLink } from 'app/shared/constants/links.const';

const serverCerfiticateValidation: ControlMetadata = {
    type: 'radio',
    bind: 'enableServerCertificateValidation',
    label: stringResources.ModelName_EnableServerCertificateValidation,
    description: stringResources.HttpEnableServerCertificateValidationDescription,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.Enable,
            value: true
        },
        {
            label: stringResources.Disable,
            value: false
        }
    ],
    defaultValue: true
};

const defaultAuthenticationOptionsRestEnabled: Array<ISelectOption<string>> = [
    {
        label: stringResources.Anonymous_DisplayText,
        value: restAuthTypes.Anonymous
    },
    {
        label: stringResources.Basic_DisplayText,
        value: restAuthTypes.Basic
    },
    {
        label: stringResources.AuthenticationType_ServicePrincipal,
        value: restAuthTypes.AadServicePrincipal
    },
    {
        label: stringResources.AuthenticationType_OAuth2ClientCredential,
        value: restAuthTypes.OAuth2ClientCredential
    },
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: restAuthTypes.ManagedServiceIdentity
    },
    {
        label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
        value: restAuthTypes.UserAssignedManagedIdentity
    }
];

const restServiceAuthInput: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'aadResourceId',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_AADResourceId,
            placeholder: 'E.g. https://management.core.windows.net',
            isLabelVertical: true
        }
    },
    serverCerfiticateValidation,
    {
        bannerMessage: '{ authHeaders&&authHeaders.length > 0 ? "" : authHeaderBannerMessage }',
        ...getAuthHeader(false)
    }
];

const servicePrincipalIdInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'servicePrincipalID',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    }
};

const spnKeyAkvSwitcherInput: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            isLabelVertical: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        }
    },
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.RestService_DisplayText),
        stringResources.ModelName_ServicePrincipalKey)
};


const spSelector: ControlMetadata = {
    type: 'radio-switch',
    bind: 'servicePrincipalCredentialType',
    hide: '{ isOldServicePrincipalResource }',
    label: stringResources.ModelName_ServicePrincipalCredentialType,
    isLabelVertical: true,
    nested: 1,
    options: [
        {
            label: stringResources.ModelName_ServicePrincipalCert,
            value: servicePrincipalCredentialTypes.ServicePrincipalCert
        },
        {
            label: stringResources.ModelName_ServicePrincipalKey,
            value: servicePrincipalCredentialTypes.ServicePrincipalKey
        }
    ],
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    defaultValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
    cases: [
        {
            caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
            children: [spnKeyAkvSwitcherInput]
        },
        {
            caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
            children: [
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    bind: 'servicePrincipalEmbeddedCert',
                    label: stringResources.ModelName_ServicePrincipalCert,
                    child: {
                        type: 'parameterizable',
                        attribute: 'servicePrincipalEmbeddedCert',
                        disableAddParameter: true,
                        child: {
                            type: 'key-file-content',
                            label: stringResources.ModelName_ServicePrincipalCert,
                            required: true,
                            isLabelVertical: true
                        }
                    },
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ],
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.RestService_DisplayText),
                        stringResources.ModelName_ServicePrincipalCert)
                },
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    bind: 'servicePrincipalEmbeddedCertPassword',
                    label: stringResources.ModelName_ServicePrincipalCertPassword,
                    child: {
                        type: 'parameterizable',
                        attribute: 'servicePrincipalEmbeddedCertPassword',
                        disableAddParameter: true,
                        child: {
                            type: 'password',
                            label: stringResources.ModelName_ServicePrincipalCertPassword,
                            isLabelVertical: true
                        }
                    },
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.RestService_DisplayText),
                        stringResources.ModelName_ServicePrincipalCertPassword)
                },

            ]
        }
    ]
};

const tenantInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    }
};

const aadResourceIdInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'aadResourceId',
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        label: stringResources.ModelName_AADResourceId,
        placeholder: 'E.g. https://management.core.windows.net',
        isLabelVertical: true
    }
};

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function restServiceAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return ({
        type: 'radio-switch',
        bind: 'authReferenceMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: restAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: restAuthRefTypes.InlineRefTypeAuthentication,
                children: (credentialTypeFilter === CredentialTypeOptions.ServicePrincipal) ? [
                    // service Principal
                    servicePrincipalIdInput,
                    spSelector,
                    { hide: '{!isOldServicePrincipalResource}', ...spnKeyAkvSwitcherInput },
                    tenantInput,
                    aadResourceIdInput,
                    parameterizableAzureCloudTypeMetadata,
                    serverCerfiticateValidation,
                    {
                        bannerMessage: '{ authHeaders&&authHeaders.length > 0 ? "" : authHeaderBannerMessage }',
                        ...getAuthHeader(false)
                    }
                ] : [...restServiceAuthInput]
            },
            {
                caseValue: restAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        supportFic: GlobalVariables.supportFic,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ]
                    },
                    ...restServiceAuthInput
                ]
            }
        ]
    });
}

const dynamicContentControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'url',
            label: stringResources.ModelName_BaseUrl,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
            isLabelVertical: true
        },
        warningMessageCalculator: (value: string) =>
            stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        options: mergeUserAuthCredentialOption(defaultAuthenticationOptionsRestEnabled, restAuthTypes.UserAuth),
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        defaultValue: 'Basic',
        cases: [
            {
                caseValue: restAuthTypes.Anonymous,
                children: [
                    serverCerfiticateValidation,
                    {
                        bannerMessage: '{ authHeaders&&authHeaders.length > 0 ? "" : authHeaderBannerMessage }',
                        ...getAuthHeader(true)
                    }
                ]
            },
            {
                caseValue: restAuthTypes.Basic,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.RestService_DisplayText),
                            stringResources.ModelName_Password)
                    },
                    serverCerfiticateValidation,
                    {
                        bannerMessage: '{ authHeaders&&authHeaders.length > 0 ? "" : authHeaderBannerMessage }',
                        ...getAuthHeader(false)
                    }
                ]
            },
            {
                caseValue: restAuthTypes.AadServicePrincipal,
                children: [
                    restServiceAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)
                ]
            },
            {
                caseValue: restAuthTypes.ManagedServiceIdentity,
                children: !GlobalVariables.isSamiSupportEnabled
                    ? [...restServiceAuthInput]
                    : [restServiceAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
            },
            {
                caseValue: restAuthTypes.UserAssignedManagedIdentity,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ]
                    },
                    ...restServiceAuthInput
                ]
            },
            {
                caseValue: restAuthTypes.OAuth2ClientCredential,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'tokenEndpoint',
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            label: stringResources.TokenEndpoint,
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'clientID',
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            label: stringResources.ModelName_ClientId,
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'clientSecret',
                        label: stringResources.ModelName_ClientSecret,
                        child: {
                            type: 'parameterizable',
                            attribute: 'clientSecret',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_ClientSecret,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.RestService_DisplayText),
                            stringResources.ModelName_ClientSecret)
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'scope',
                            label: stringResources.Scope,
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'resource',
                            label: stringResources.Resource,
                            isLabelVertical: true
                        }
                    },
                    serverCerfiticateValidation,
                    {
                        bannerMessage: '{ authHeaders&&authHeaders.length > 0 ? "" : authHeaderBannerMessage }',
                        ...getAuthHeader(false)
                    }
                ]
            },
            {
                caseValue: restAuthTypes.UserAuth,
                children: [
                    {
                        type: 'credential-select',
                        hide: !GlobalVariables.enableConnectorUserAuthCred,
                        bind: 'credential',
                        filterCredentialType: CredentialTypeOptions.UserAuth,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                ]
            }
        ]
    }
];

const dataflowRestSource: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.Rest,
    settings: adfModelTypeMap.rest.dataflowSettings,
    controls: [
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'requestMethod',
                label: stringResources.Dataset_RequestMethod,
                options: [
                    {
                        label: stringResources.Dataset_RequestMethod_Get,
                        value: 'GET'
                    },
                    {
                        label: stringResources.Method_Post,
                        value: 'POST'
                    }
                ],
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                defaultValue: 'GET',
                description: stringResources.Dataset_RequestMethodDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textarea',
                bind: 'requestBody',
                label: stringResources.Dataset_RequestBody,
                description: stringResources.Dataset_RequestBodyDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            hide: `{ requestMethod.expression === 'GET' }`,
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'requestEntity',
                label: stringResources.Dataset_RelativeUrl,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestTimeout',
                label: `${stringResources.ModelName_Request_Timeout} (s)`,
                description: stringResources.DataFlow_HttpRequestTimeoutDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestInterval',
                label: `${stringResources.ModelName_REST_Source_Request_Interval} (ms)`,
                description: stringResources.REST_CopySource_Request_Interval_Description,
                min: 0,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameter-config',
            bind: 'additionalHeaders',
            label: stringResources.Dataset_AdditionalHeaders,
            keyWarningMessageFactory: (item: string, owner: Owned) => {
                if (!!item) {
                    if (item.toLowerCase() === 'accept') {
                        return stringResources.DataFlow_Rest_Additional_Headers_Warning;
                    }
                    if (item.toLowerCase() === 'content-type' && (owner instanceof DataFlowSource || owner instanceof DataFlowSink)
                        && owner.datasetSettings instanceof RestSettings && !owner.datasetSettings.requestBody.expression) {
                        return stringResources.Rest_ContentType_Header_Warning;
                    }
                }
                return null;
            },
            description: stringResources.Dataset_AdditionalHeadersDesc,
            hintMode: 'info',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'parameter-config',
            bind: 'queryParameters',
            label: stringResources.Query_Parameters,
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'rest-pagination-rules',
            bind: 'paginationRules',
            label: stringResources.Dataset_PaginationRules,
            description: stringResources.Dataset_PaginationRulesDescription,
            descriptionLink: 'https://go.microsoft.com/fwlink/?linkid=2148868',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'radio-switch',
            label: stringResources.Format_Label,
            bind: 'restDataFormat',
            options: [
                {
                    label: stringResources.ModelName_JsonDataset,
                    value: 'json'
                },
                {
                    label: stringResources.ModelName_XmlDataset,
                    value: 'xml'
                },
                {
                    label: stringResources.None,
                    value: undefined
                }
            ],
            cases: [
                {
                    caseValue: 'json',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsJsonAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'parameterizable',
                                    child: {
                                        type: 'radio',
                                        label: stringResources.DataFlowSourceSettingsDocumentFormLabel,
                                        description: stringResources.DataFlowSourceSettingsDocumentFormDescription,
                                        options: documentFormOptions,
                                        bind: 'documentForm',
                                        isDataFlowExpression: true
                                    },
                                    dynamicContentTextClass: 'input-spaced',
                                    showErrorIcon: false,
                                    isDataFlowExpression: true
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: 'xml',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsXmlAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'textbox',
                                    label: stringResources.NullValueFieldLabel,
                                    bind: 'nullValue'
                                },
                                {
                                    type: 'xml-namespace-prefix',
                                    bind: {
                                        namespacePrefixes: 'namespacePrefixes',
                                        namespaces: 'namespaces'
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: undefined,
                    children: []
                }
            ]
        }
    ],
    useInlineProjection: true
};

const dataflowRestSink: IDataflowTypeMetadataSink = {
    type: DatasetFormatType.Rest,
    settings: adfModelTypeMap.rest.dataflowSettings,
    controls: [
        {
            type: 'rest-update-settings',
            bind: {
                allowInsert: 'allowInsert',
                allowDelete: 'allowDelete',
                allowUpsert: 'allowUpsert',
                allowUpdate: 'allowUpdate',
                insertHttpMethod: 'insertHttpMethod',
                deleteHttpMethod: 'deleteHttpMethod',
                upsertHttpMethod: 'upsertHttpMethod',
                updateHttpMethod: 'updateHttpMethod',
                rowRelativeUrl: 'rowRelativeUrl',
                skipRowRelativeUrl: 'skipRowRelativeUrl'
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'requestEntity',
                label: stringResources.Dataset_RelativeUrl,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'httpCompressionType',
                label: stringResources.ModelName_HttpCompressionType,
                options: [
                    {
                        label: stringResources.None,
                        value: undefined
                    },
                    {
                        label: stringResources.Dataset_CompressionType_Option_GZip,
                        value: compressionCodecType.gzip
                    }
                ],
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'batchSize',
                label: stringResources.ModelName_BatchSize,
                description: stringResources.DataFlow_BatchSize_Description,
                isDataFlowExpression: true
            },
            hide: `{ !restDataFormat }`,
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestTimeout',
                label: `${stringResources.ModelName_Request_Timeout} (s)`,
                description: stringResources.DataFlow_HttpRequestTimeoutDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestInterval',
                label: `${stringResources.ModelName_REST_Source_Request_Interval} (ms)`,
                description: stringResources.REST_CopySource_Request_Interval_Description,
                min: 0,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameter-config',
            bind: 'additionalHeaders',
            label: stringResources.Dataset_AdditionalHeaders,
            keyWarningMessageFactory: (item: string) => {
                return (!!item && item.toLowerCase() === 'content-encoding') ?
                    stringResources.DataFlow_Rest_Sink_Additional_Headers_Encoding_Warning :
                    (!!item && item.toLowerCase() === 'content-type') ? stringResources.DataFlow_Rest_Sink_Additional_Headers_Content_Warning : undefined;
            },
            description: stringResources.Dataset_AdditionalHeadersDesc,
            hintMode: 'info',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'parameter-config',
            bind: 'queryParameters',
            label: stringResources.Query_Parameters,
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'radio-switch',
            label: stringResources.Format_Label,
            bind: 'restDataFormat',
            options: [
                {
                    label: stringResources.ModelName_JsonDataset,
                    value: 'json'
                },
                {
                    label: stringResources.None,
                    value: undefined
                }
            ],
            cases: [
                {
                    caseValue: 'json',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsJsonAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'select',
                                    label: stringResources.DateFormat_Label,
                                    options: DataFlowTypesUtils.commonFormats('date').options.map((format: string) => {
                                        return {
                                            label: format,
                                            value: format
                                        };
                                    }),
                                    bind: 'dateFormat'
                                },
                                {
                                    type: 'select',
                                    label: stringResources.Timestamp_Format_Label,
                                    options: DataFlowTypesUtils.commonFormats('timestamp').options.map((format: string) => {
                                        return {
                                            label: format,
                                            value: format
                                        };
                                    }),
                                    bind: 'timestampFormat'
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: undefined,
                    children: []
                }
            ]
        }
    ]
};

const dataflowRestCallSourceUpdated: IDataflowTypeMetadataSource = {
    type: DatasetFormatType.RestCall,
    settings: adfModelTypeMap.restCall.dataflowSettings,
    notShowInTypeSelection: true,
    controls: [
        {
            type: 'parameterizable',
            child: {
                type: 'select',
                bind: 'requestMethod',
                label: stringResources.Dataset_RequestMethod,
                options: [
                    {
                        label: stringResources.Dataset_RequestMethod_Get,
                        value: 'GET'
                    },
                    {
                        label: stringResources.Method_Post,
                        value: 'POST'
                    },
                    {
                        label: stringResources.Dataset_RequestMethod_Patch,
                        value: 'PATCH'
                    },
                    {
                        label: stringResources.Dataset_RequestMethod_Put,
                        value: 'PUT'
                    },
                    {
                        label: stringResources.Dataset_RequestMethod_Delete,
                        value: 'DELETE'
                    }
                ],
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                defaultValue: 'GET',
                description: stringResources.CallTransform_RequestMethodDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'requestEntity',
                label: stringResources.Dataset_RelativeUrl,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true
        },
        {
            type: 'row-relative-url-settings',
            bind: {
                rowRelativeUrl: 'rowRelativeUrl',
                skipRowRelativeUrl: 'skipRowRelativeUrl'
            }
        },
        {
            type: 'checkbox',
            bind: 'cache',
            label: stringResources.DataFlowCallCache,
            description: stringResources.ModelName_REST_Cache_Description,
            defaultValue: false
        },
        {
            type: 'select',
            bind: 'httpCompressionType',
            label: stringResources.ModelName_HttpCompressionType,
            options: [
                {
                    label: stringResources.None,
                    value: undefined
                },
                {
                    label: stringResources.Dataset_CompressionType_Option_GZip,
                    value: compressionCodecType.gzip
                }
            ],
            isDataFlowExpression: true
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestTimeout',
                label: `${stringResources.ModelName_Request_Timeout} (s)`,
                description: stringResources.DataFlow_HttpRequestTimeoutDescription,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'requestInterval',
                label: `${stringResources.ModelName_REST_Source_Request_Interval} (ms)`,
                description: stringResources.REST_CopySource_Request_Interval_Description,
                min: 0,
                isDataFlowExpression: true
            },
            dynamicContentTextClass: 'input-spaced',
            showErrorIcon: false,
            isDataFlowExpression: true,
            expectedReturnType: 'integer'
        },
        {
            type: 'parameter-config',
            bind: 'additionalHeaders',
            label: stringResources.Dataset_AdditionalHeaders,
            keyWarningMessageFactory: (item: string, owner: Owned) => {
                if (!!item) {
                    if (item.toLowerCase() === 'accept') {
                        return stringResources.DataFlow_Rest_Additional_Headers_Warning;
                    }
                    if (item.toLowerCase() === 'content-type' && (owner instanceof DataFlowSource || owner instanceof DataFlowSink)
                        && owner.datasetSettings instanceof RestCallSettings && !owner.datasetSettings.requestBody.expression) {
                        return stringResources.Rest_ContentType_Header_Warning;
                    }
                }
                return null;
            },
            description: stringResources.Dataset_AdditionalHeadersDesc,
            hintMode: 'info',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'parameter-config',
            bind: 'queryParameters',
            label: stringResources.Query_Parameters,
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'parameter-config',
            bind: 'paginationRules',
            label: stringResources.Dataset_PaginationRules,
            description: stringResources.Dataset_PaginationRulesDescription,
            descriptionLink: 'https://go.microsoft.com/fwlink/?linkid=2148868',
            isLayoutVertical: false,
            dataFlowVersion: true,
            nameParameterizable: true
        },
        {
            type: 'radio-switch',
            label: stringResources.Format_Label,
            bind: 'restResponseDataFormat',
            options: [
                {
                    label: stringResources.ModelName_JsonDataset,
                    value: 'json'
                },
                {
                    label: stringResources.ModelName_XmlDataset,
                    value: 'xml'
                },
                {
                    label: stringResources.None,
                    value: undefined
                }
            ],
            cases: [
                {
                    caseValue: 'json',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsJsonAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'parameterizable',
                                    child: {
                                        type: 'radio',
                                        label: stringResources.DataFlowSourceSettingsDocumentFormLabel,
                                        description: stringResources.DataFlowSourceSettingsDocumentFormDescription,
                                        options: documentFormOptions,
                                        bind: 'documentForm',
                                        isDataFlowExpression: true
                                    },
                                    dynamicContentTextClass: 'input-spaced',
                                    showErrorIcon: false,
                                    isDataFlowExpression: true
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: 'xml',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsXmlAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'textbox',
                                    label: stringResources.NullValueFieldLabel,
                                    bind: 'nullValue'
                                },
                                {
                                    type: 'xml-namespace-prefix',
                                    bind: {
                                        namespacePrefixes: 'namespacePrefixes',
                                        namespaces: 'namespaces'
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: undefined,
                    children: []
                }
            ]
        },
        {
            type: 'radio-switch',
            label: stringResources.Format_Label,
            bind: 'restDataFormat',
            options: [
                {
                    label: stringResources.ModelName_JsonDataset,
                    value: 'json'
                },
                {
                    label: stringResources.None,
                    value: undefined
                }
            ],
            cases: [
                {
                    caseValue: 'json',
                    children: [
                        {
                            type: 'accordion',
                            header: stringResources.DataFlowSourceSettingsJsonAccordionHeader,
                            emitValueChange: false,
                            children: [
                                {
                                    type: 'select',
                                    label: stringResources.DateFormat_Label,
                                    options: DataFlowTypesUtils.commonFormats('date').options.map((format: string) => {
                                        return {
                                            label: format,
                                            value: format
                                        };
                                    }),
                                    bind: 'dateFormat'
                                },
                                {
                                    type: 'select',
                                    label: stringResources.Timestamp_Format_Label,
                                    options: DataFlowTypesUtils.commonFormats('timestamp').options.map((format: string) => {
                                        return {
                                            label: format,
                                            value: format
                                        };
                                    }),
                                    bind: 'timestampFormat'
                                }
                            ]
                        }
                    ]
                },
                {
                    caseValue: undefined,
                    children: []
                }
            ]
        },
    ]
};

export const restServiceMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.rest.linkedServiceType,
        displayName: stringResources.RestService_DisplayText,
        ...(!__IS_FABRIC_DPE__ ? {
            showWarningMessage: '{ isSharePointUrl && authenticationType === "AadServicePrincipal" && (isOldServicePrincipalResource || servicePrincipalCredentialType === "ServicePrincipalKey") }',
            warningMessage: format(
                stringResources.Connector_SharePointSpn_RetirementWarning,
                stringResources.RestService_DisplayText),
            warningMessageHyperlink: sharePointSpnRetirementDocLink
        } : {}),
        linkedServiceKind: 'hierarchical',
        icon: {
            className: 'svg-i-100x100-REST'
        },
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'RestService' as ResourceKind
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Anonymous, ConnectionAuthKind.Basic, ConnectionAuthKind.OAuth2, ConnectionAuthKind.ServicePrincipal],
        isFirstClass: true,
        copySinkTypes: [adfModelTypeMap.rest.copySinkType],
        supportDatasetV2: true,
        copySourceTypes: [adfModelTypeMap.rest.copySourceType],
        category: ['GenericProtocol'],
        supportFirstClassParameterExperience: true,
        controls: dynamicContentControls,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        supportOPDG: GlobalVariables.enableOPDG,
        dataflowSource: [dataflowRestSource, dataflowRestCallSourceUpdated],
        dataflowSink: [dataflowRestSink],
        additionalPropertiesInFabric: GlobalVariables.disableServerCertificate ? [
            {
                label: 'Disable server certificate validation',
                value: 'disableServerCertificateValidation',
                type: Type.Boolean
            }
        ] : undefined
    };

    return metadata;
};
